import { useEffect, useState } from "react";
import { ApiPromise, WsProvider } from "@polkadot/api";
import { ContractPromise } from "@polkadot/api-contract";
import { hexToString } from "@polkadot/util";
import { encodeAddress } from "@polkadot/keyring";
import { Link } from "react-router-dom";
import { formatAddress } from "../../utils/format";
import LoadingSpinner from "../loadingSpinner";

export default function TokenHolders({ contract, address }) {
  const [holders, setHolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contract || !address || !contract.abi) {
      setLoading(false);
      return;
    }

    fetchTokenHolders();
  }, [address]); // Only depend on address, not the entire contract object

  const fetchTokenHolders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Connect to chain
      const wsProvider = new WsProvider(
        process.env.REACT_APP_WS_URL || "ws://127.0.0.1:9944",
      );
      const api = await ApiPromise.create({ provider: wsProvider });

      // Parse contract with ABI
      const contractInstance = new ContractPromise(api, contract.abi, address);

      // Extract Transfer events to get all token recipients
      const holderMap = new Map();

      // Get events from contract
      if (contract.events && Array.isArray(contract.events)) {
        console.log("Processing", contract.events.length, "events");
        for (const event of contract.events) {
          console.log("Event:", event.name, event.args);

          // Look for Transfer or PurchaseIntent events
          if (event.name === "Transfer" && event.args) {
            // Handle both array format and object format
            let to, amount;
            if (Array.isArray(event.args)) {
              to = event.args.find((arg) => arg.name === "to")?.value;
              amount = event.args.find((arg) => arg.name === "amount")?.value;
            } else if (event.argsObject) {
              to = event.argsObject.to;
              amount = event.argsObject.amount;
            }

            if (to && to !== null) {
              // Convert hex address to SS58 format
              let ss58Address = to;
              try {
                if (to.startsWith("0x")) {
                  ss58Address = encodeAddress(to, 42); // 42 is the standard Substrate SS58 prefix
                }
              } catch (e) {
                console.warn("Failed to encode address:", e);
              }

              if (!holderMap.has(ss58Address)) {
                holderMap.set(ss58Address, {
                  address: ss58Address,
                  balance: 0,
                  txCount: 0,
                });
              }
              holderMap.get(ss58Address).txCount++;
              if (amount) {
                holderMap.get(ss58Address).balance += Number(amount);
              }
            }
          } else if (event.name === "PurchaseIntent" && event.args) {
            // Handle both array format and object format
            let buyer, amount;
            if (Array.isArray(event.args)) {
              buyer = event.args.find((arg) => arg.name === "buyer")?.value;
              amount = event.args.find((arg) => arg.name === "amount")?.value;
            } else if (event.argsObject) {
              buyer = event.argsObject.buyer;
              amount = event.argsObject.amount;
            }

            if (buyer) {
              // Convert hex address to SS58 format
              let ss58Address = buyer;
              try {
                if (buyer.startsWith("0x")) {
                  ss58Address = encodeAddress(buyer, 42); // 42 is the standard Substrate SS58 prefix
                }
              } catch (e) {
                console.warn("Failed to encode address:", e);
              }

              if (!holderMap.has(ss58Address)) {
                holderMap.set(ss58Address, {
                  address: ss58Address,
                  balance: 0,
                  txCount: 0,
                });
              }
              holderMap.get(ss58Address).txCount++;
              if (amount) {
                holderMap.get(ss58Address).balance += Number(amount);
              }
            }
          }
        }
        console.log("Token holders found:", holderMap.size);
      }

      // Query balances for each holder if contract has balanceOf method
      const gasLimit = api.registry.createType("WeightV2", {
        refTime: 100_000_000_000n,
        proofSize: 1_000_000n,
      });

      const holdersList = Array.from(holderMap.values());

      // Try to query balances
      for (const holder of holdersList) {
        try {
          if (contractInstance.query.balanceOf) {
            const { output } = await contractInstance.query.balanceOf(
              address,
              { gasLimit },
              holder.address,
            );

            const result = output?.toJSON();
            if (result && typeof result.ok !== "undefined") {
              holder.balance = Number(result.ok);
            }
          }
        } catch (e) {
          console.warn(`Failed to query balance for ${holder.address}:`, e);
          // Keep the balance from events
        }
      }

      // Fetch DID information
      console.log("Fetching DID information for holders...");
      for (const holder of holdersList) {
        try {
          const didInfo = await api.query.did?.dids(holder.address);
          if (didInfo && !didInfo.isEmpty) {
            const didData = didInfo.toJSON();
            console.log(
              `Full DID data structure for ${holder.address}:`,
              JSON.stringify(didData, null, 2),
            );

            // Try to find the actual name field in the DID data
            let didName = "Registered";

            // The name field is hex-encoded, so we need to decode it
            if (didData?.name) {
              try {
                didName = hexToString(didData.name);
                console.log(`Decoded DID name from hex: ${didName}`);
              } catch (e) {
                console.warn("Failed to decode DID name:", e);
                didName = didData.name;
              }
            }

            holder.did = didName;
            holder.hasDid = true;
            console.log(`DID name for ${holder.address}: ${holder.did}`);
          } else {
            holder.hasDid = false;
            console.log(`No DID for ${holder.address}`);
          }
        } catch (e) {
          console.warn(`Failed to query DID for ${holder.address}:`, e);
          holder.hasDid = false;
        }
      }

      // Sort by balance descending
      holdersList.sort((a, b) => b.balance - a.balance);

      setHolders(holdersList);
      await api.disconnect();
    } catch (err) {
      console.error("Error fetching token holders:", err);
      setError(err.message || "Failed to fetch token holders");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <LoadingSpinner />
        <p style={{ marginTop: "16px", color: "#6b7280" }}>
          Loading token holders...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          backgroundColor: "#fef2f2",
          borderRadius: "8px",
          border: "1px solid #fecaca",
        }}
      >
        <p style={{ color: "#dc2626", fontWeight: "500" }}>Error: {error}</p>
        <button
          onClick={fetchTokenHolders}
          style={{
            marginTop: "16px",
            padding: "8px 16px",
            backgroundColor: "#00A786",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!contract.abi) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          backgroundColor: "#fef3c7",
          borderRadius: "8px",
          border: "1px solid #fde68a",
        }}
      >
        <p style={{ color: "#92400e", fontWeight: "500" }}>
          Contract ABI not available. Token holder information requires the
          contract ABI.
        </p>
        <p style={{ color: "#78350f", marginTop: "8px", fontSize: "14px" }}>
          This feature requires the contract ABI to be uploaded.
        </p>
      </div>
    );
  }

  if (holders.length === 0) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          backgroundColor: "#f9fafb",
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
        }}
      >
        <p style={{ color: "#6b7280", fontSize: "16px" }}>
          No token holders found
        </p>
        <p style={{ color: "#9ca3af", marginTop: "8px", fontSize: "14px" }}>
          Debug:{" "}
          {contract.events
            ? `${contract.events.length} events found`
            : "No events"}{" "}
          | ABI: {contract.abi ? "Yes" : "No"}
        </p>
        <p style={{ color: "#9ca3af", marginTop: "8px", fontSize: "14px" }}>
          This contract has no transfer or purchase events yet.
        </p>
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          marginBottom: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#111827" }}>
          Token Holders ({holders.length})
        </h3>
        <button
          onClick={fetchTokenHolders}
          style={{
            padding: "6px 12px",
            backgroundColor: "#f3f4f6",
            color: "#374151",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
          }}
        >
          Refresh
        </button>
      </div>

      <div
        style={{
          maxHeight: "600px",
          overflowY: "auto",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          backgroundColor: "white",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead
            style={{
              backgroundColor: "#f9fafb",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <tr>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b7280",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Rank
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b7280",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Address
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b7280",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                DID Status
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "right",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b7280",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Balance
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "right",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b7280",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Transactions
              </th>
            </tr>
          </thead>
          <tbody>
            {holders.map((holder, index) => (
              <tr
                key={holder.address}
                style={{
                  borderBottom: "1px solid #f3f4f6",
                  transition: "background-color 0.15s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f9fafb")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "white")
                }
              >
                <td
                  style={{
                    padding: "12px 16px",
                    fontSize: "14px",
                    color: "#6b7280",
                  }}
                >
                  #{index + 1}
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <Link
                    to={`/accounts/${holder.address}`}
                    style={{
                      fontFamily: "Overpass Mono, monospace",
                      fontSize: "14px",
                      color: "#00868A",
                      textDecoration: "none",
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.target.style.color = "#00A786")}
                    onMouseLeave={(e) => (e.target.style.color = "#00868A")}
                  >
                    {formatAddress(holder.address)}
                  </Link>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  {holder.hasDid ? (
                    <div>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          backgroundColor: "#dcfce7",
                          color: "#15803d",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                      >
                        Verified
                      </span>
                      {holder.did && (
                        <div
                          style={{
                            marginTop: "4px",
                            fontSize: "13px",
                            color: "#374151",
                          }}
                        >
                          <strong>DID Name:</strong> {holder.did}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "4px 8px",
                        backgroundColor: "#fee2e2",
                        color: "#991b1b",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                    >
                      No DID
                    </span>
                  )}
                </td>
                <td
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#111827",
                  }}
                >
                  {holder.balance.toLocaleString()}
                </td>
                <td
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontSize: "14px",
                    color: "#6b7280",
                  }}
                >
                  {holder.txCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
